import React, { useMemo, useState } from 'react';
import { CardHeader, CardSection, Gutter, Suspense } from '@axiom/ui';
import type {
  Candidate,
  LimitedCandidate,
  Taxonomy,
  AfcCandidateSearch,
  SavedCandidate,
  User,
} from '@axiom/validation';

import { TalentHighlights } from '../TalentHighlights/TalentHighlights';
import { TalentAbout } from '../TalentAbout/TalentAbout';
import { TalentBadges } from '../TalentBadges/TalentBadges';
import { TalentPricing } from '../TalentPricing/TalentPricing';
import { TalentCardHeader } from '../TalentCardHeader/TalentCardHeader';
import { TalentDirectInterviewFlow } from '../TalentDirectInterviewFlow/TalentDirectInterviewFlow';
import { isLoggedInUser, isLpUser } from '../../utils/roles-util';
import { CandidateUtil } from '../../utils/candidate-util';
import { PricingUtil } from '../../utils/pricing-util';

type CandidateType =
  | Candidate
  | LimitedCandidate
  | AfcCandidateSearch
  | SavedCandidate['candidate'];

export type TalentCardContentProps = {
  candidate: CandidateType;
  user: User | null | undefined;
  taxonomy: Taxonomy;
  isPricingShown?: boolean;
  onLoggedOutRequestInterview?: () => void;
};

export const TalentCardContent = ({
  candidate,
  user,
  taxonomy,
  isPricingShown,
  onLoggedOutRequestInterview,
}: TalentCardContentProps) => {
  const [showInterviewFlow, setShowInterviewFlow] = useState(false);

  const isUserLoggedIn = !!(user && isLoggedInUser(user));
  const showFavoriteButton = isUserLoggedIn && !isLpUser(user);
  const showRequestInterviewButton =
    candidate.isInterviewableFromALT && !isLpUser(user);

  const formattedLocation = useMemo(
    () => CandidateUtil.formatCandidateLocation(candidate),
    [candidate]
  );

  const pricingData = useMemo(
    () => PricingUtil.getPricingData(candidate, user),
    [candidate, user, isPricingShown]
  );

  const { pricingAvailable, fullTimeDailyRate, adHocHourlyRate } = pricingData;

  return (
    <>
      <CardHeader>
        <TalentCardHeader
          candidate={candidate}
          user={user}
          showFavoriteButton={showFavoriteButton}
          taxonomy={taxonomy}
          showRequestInterviewButton={showRequestInterviewButton}
          onRequestInterview={
            showRequestInterviewButton
              ? () => {
                  if (user) {
                    setShowInterviewFlow(true);
                  } else {
                    onLoggedOutRequestInterview?.();
                  }
                }
              : undefined
          }
        />
      </CardHeader>
      <CardSection divider>
        <TalentBadges candidate={candidate} />
        {pricingAvailable && isPricingShown ? (
          <Gutter top="16px">
            <TalentPricing
              candidate={candidate}
              taxonomy={taxonomy}
              fullTimeDailyRate={fullTimeDailyRate}
              adHocHourlyRate={adHocHourlyRate}
            />
          </Gutter>
        ) : null}
        {!!candidate.candidateSummary && (
          <Gutter top="16px">
            <TalentAbout candidate={candidate} />
          </Gutter>
        )}
        <Gutter top="16px">
          <TalentHighlights
            candidate={candidate}
            location={formattedLocation}
            taxonomy={taxonomy}
          />
        </Gutter>
      </CardSection>
      {showInterviewFlow && user ? (
        <Suspense fallback={null}>
          <TalentDirectInterviewFlow
            name="TALENT_DIRECT_INTERVIEW_FLOW"
            candidate={candidate}
            taxonomy={taxonomy}
            user={user}
            onExitInterviewFlow={() => {
              setShowInterviewFlow(false);
            }}
          />
        </Suspense>
      ) : null}
    </>
  );
};
